'use client';

import { Download } from 'lucide-react';
import { useState } from 'react';
import { useTranslations } from 'next-intl';

interface DownloadPdfButtonProps {
  docKey: string;
  locale: string;
  variant?: 'primary' | 'secondary';
  className?: string;
}

export default function DownloadPdfButton({
  docKey,
  locale,
  variant = 'primary',
  className = '',
}: DownloadPdfButtonProps) {
  const [isDownloading, setIsDownloading] = useState(false);
  const t = useTranslations('Legal');

  const handleDownload = async () => {
    try {
      setIsDownloading(true);
      
      console.log('Starting PDF download for:', docKey, 'locale:', locale);

      // Call the enhanced PDF generator API
      const apiUrl = `/${locale}/legal/download-v2?doc=${docKey}`;
      console.log('Fetching PDF from:', apiUrl);
      
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Accept': 'application/pdf',
        },
      });

      console.log('Response status:', response.status);
      console.log('Response headers:', Object.fromEntries(response.headers.entries()));

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('PDF generation failed:', errorData);
        throw new Error(errorData.details || 'Failed to generate PDF');
      }

      // Get the PDF blob
      const blob = await response.blob();

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from Content-Disposition header or use default
      const contentDisposition = response.headers.get('Content-Disposition');
      const filename = contentDisposition
        ? contentDisposition.split('filename=')[1]?.replace(/"/g, '')
        : `CISCODE_${docKey}_${locale}.pdf`;

      link.download = filename;
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

    } catch (error) {
      console.error('Download error:', error);
      alert('Failed to download PDF. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  const baseClasses = `
    inline-flex items-center gap-2 px-6 py-3 rounded-lg font-medium
    transition-all duration-300 ease-out
    disabled:opacity-50 disabled:cursor-not-allowed
  `;

  const variantClasses = variant === 'primary'
    ? 'bg-gradient-to-r from-[#08264C] to-[#3F74EF] text-white hover:shadow-lg hover:scale-105'
    : 'bg-white text-[#08264C] border-2 border-[#08264C] hover:bg-[#08264C] hover:text-white';

  return (
    <button
      onClick={handleDownload}
      disabled={isDownloading}
      className={`${baseClasses} ${variantClasses} ${className}`}
      aria-label={isDownloading ? 'Downloading PDF...' : t('download')}
    >
      <Download className={`w-5 h-5 ${isDownloading ? 'animate-bounce' : ''}`} />
      <span>
        {isDownloading ? 'Generating...' : t('download')}
      </span>
    </button>
  );
}
